import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useDebounce from 'hooks/useDebounce';
import { useState } from 'react';
import type { ServiceCenter } from 'services/api/businessUnits.api';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, SURFACE_TINT, YELLOW } from 'styles/tokens';

interface Props {
  branches: ServiceCenter[];
  counts: Record<string, number>;
  isLoading: boolean;
  appointmentsLoading: boolean;
  onSelect: (branch: string) => void;
}

const CARD_WIDTH = { xs: '100%', sm: 400 };

const BranchPicker = ({ branches, counts, isLoading, appointmentsLoading, onSelect }: Props) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const term = debouncedSearch.trim().toLowerCase();
  const filtered = term
    ? branches.filter((b) => `${b.name} ${b.address ?? ''}`.toLowerCase().includes(term))
    : branches;

  return (
    <Box
      sx={{
        px: 2,
        py: 4,
        mx: 'auto',
        maxWidth: 1280,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {/* Search by service-center name or address */}
      <Box
        sx={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: '999px',
          backgroundColor: SURFACE_TINT,
          width: '100%',
          maxWidth: 440,
        }}
      >
        <SearchIcon sx={{ fontSize: 20, color: 'rgba(21, 24, 58, 0.4)' }} />
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o dirección…"
          sx={{ fontSize: 14, flex: 1, color: NAVY }}
        />
      </Box>

      {/* Service-center cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, width: '100%' }}>
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={104}
              sx={{ borderRadius: RADIUS_CARD, width: CARD_WIDTH }}
            />
          ))}

        {!isLoading &&
          filtered.map((branch) => {
            const count = counts[branch.name] ?? 0;
            return (
              <Box
                key={branch.name}
                onClick={() => onSelect(branch.name)}
                sx={{
                  width: CARD_WIDTH,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2.5,
                  cursor: 'pointer',
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  borderRadius: RADIUS_CARD,
                  boxShadow: SHADOW,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(YELLOW, 0.18),
                    flexShrink: 0,
                  }}
                >
                  <LocationOnIcon sx={{ color: NAVY, fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: NAVY,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {branch.name}
                  </Typography>
                  {branch.address && (
                    <Typography
                      title={branch.address}
                      sx={{
                        fontSize: 13,
                        color: 'rgba(21, 24, 58, 0.55)',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {branch.address}
                    </Typography>
                  )}
                  {appointmentsLoading ? (
                    <Skeleton variant="text" width={90} height={16} sx={{ mt: 0.25 }} />
                  ) : (
                    <Typography sx={{ fontSize: 12, color: 'rgba(21, 24, 58, 0.4)', mt: 0.25 }}>
                      {count} {count === 1 ? 'cita este mes' : 'citas este mes'}
                    </Typography>
                  )}
                </Box>
                {appointmentsLoading ? (
                  <Skeleton variant="rectangular" width={30} height={24} sx={{ borderRadius: RADIUS_ITEM }} />
                ) : (
                  <Box
                    sx={{
                      minWidth: 30,
                      height: 24,
                      px: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: RADIUS_ITEM,
                      backgroundColor: count > 0 ? YELLOW : 'rgba(21, 24, 58, 0.06)',
                      color: NAVY,
                      fontSize: 12.5,
                      fontWeight: 700,
                    }}
                  >
                    {count}
                  </Box>
                )}
              </Box>
            );
          })}

        {!isLoading && filtered.length === 0 && (
          <Typography sx={{ fontSize: 14, color: 'rgba(21, 24, 58, 0.5)', mt: 2 }}>
            {branches.length === 0
              ? 'Esta unidad de negocio no tiene sucursales configuradas.'
              : 'No se encontraron sucursales para tu búsqueda.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BranchPicker;
